import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import pegasusDiverterService from '../services/pegasusDiverterService';
import IntegrationLogTable from './shared/IntegrationLogTable';
import Spinner from './shared/Spinner';
import './OxiDequeueAdmin.css';
import './AdminSyncConnectQueries.css';

// Admin › Infra › Pegasus Diverter › Logs — what the hub saw from one diverted source, live.
//
// A FULL PAGE, not a modal (STYLE.md: a modal hides content behind an overlay, breaks the back button and cannot be
// linked). It lists the integration log's records for the source's hotel code that came through the Pegasus proxy —
// received, refused, answered — in the shared log table, so each record opens to its steps exactly as on the pipeline's
// Logs tab. Auto refresh (on by default here: the page exists to watch a divert take effect) re-queries every 5 s and
// pauses while a record is open. Beside it, what the legacy stack says it forwarded for the same source, so "the hub
// never got it" and "legacy never sent it" can be told apart.

const AUTO_REFRESH_MS = 5000;
const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const errorOf = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;

const SINCE = [
    { value: 'now-1h', label: 'Last hour' },
    { value: 'now-24h', label: 'Last 24 hours' },
    { value: 'now-7d', label: 'Last 7 days' },
];

export default function AdminPegasusDiverterLogs() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [since, setSince] = useState('now-24h');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const inFlight = useRef(false);

    const load = useCallback(async ({ quiet = false } = {}) => {
        if (inFlight.current) return;
        inFlight.current = true;
        if (!quiet) setLoading(true);
        try {
            const res = await pegasusDiverterService.logs(id, { since, limit: 100 });
            setData(res.data);
            setError(res.data && res.data.error ? res.data.error : null);
        } catch (e) {
            setError(errorOf(e));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, [id, since]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!autoRefresh) return undefined;
        const t = setInterval(() => load({ quiet: true }), AUTO_REFRESH_MS);
        return () => clearInterval(t);
    }, [autoRefresh, load]);

    const groups = (data && data.groups) || [];
    const legacy = (data && data.legacy) || {};

    return (
        <div className="odq-page">
            <div className="odq-header" style={{ alignItems: 'flex-start' }}>
                <div>
                    <div className="scq-meta" style={{ marginBottom: '0.25rem' }}>
                        <Link to="/admin/pegasus-diverter">← Pegasus Diverter</Link>
                    </div>
                    <h1 className="odq-title">Diverted source — hub log</h1>
                    <p className="odq-subtitle">
                        Source <code className="scq-mono">{data ? data.source : '…'}</code>: every message the hub's Pegasus proxy
                        received, refused or answered for hotel code <strong>{data ? data.hotelCode : '…'}</strong>, newest first.
                        Open a record to see its steps. The legacy stack reports{' '}
                        <strong>{legacy.calls ? Number(legacy.calls).toLocaleString() : 'no'}</strong> message{legacy.calls === 1 ? '' : 's'} forwarded
                        {legacy.lastForwarded ? `, last at ${when(legacy.lastForwarded)}` : ''}
                        {legacy.error ? ` (legacy count unavailable: ${legacy.error})` : ''}.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select className="odq-filter-select" value={since} onChange={e => setSince(e.target.value)}>
                        {SINCE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem' }}>
                        <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                        Auto refresh (5s)
                    </label>
                    <button className="acp-btn acp-btn-secondary" onClick={() => load()}>Refresh</button>
                    <span className="scq-meta">{data && data.checkedAt ? `Updated ${new Date(data.checkedAt).toLocaleTimeString()}` : ''}</span>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error" onClick={() => setError(null)}>{error}</div>}

            <div className="acp-card">
                {loading && !groups.length ? <Spinner /> : (
                    <IntegrationLogTable
                        groups={groups}
                        showIntegration
                        emptyText={`Nothing from the Pegasus proxy for ${data ? data.hotelCode : 'this hotel code'} in this period. If the legacy stack shows the source as OK rather than FORWARDED, the message was handled there and never sent to the hub.`}
                        canResend={false}
                        canAssist
                    />
                )}
            </div>
        </div>
    );
}
